import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { withStyles, Chip, Box, Typography } from '@material-ui/core';
import { DataGrid, esES } from '@material-ui/data-grid';
import { useMonitoreo } from './MonitoreoContext';

const areaClassMap = {
    1: 'andon-table-area3', // verde
    2: 'andon-table-area1', // rojo
    3: 'andon-table-area4', // azul
    4: 'andon-table-area2', // amarillo
};

function renderArea(params) {
    const iniciales = params.row.inicialesArea || '?';
    const className = areaClassMap[params.row.colorArea] || 'andon-table-areaS';
    return (
        <div className={className}>
            <h1>{iniciales}</h1>
        </div>
    );
}

const YellowChip = withStyles({ root: { backgroundColor: '#f8eab7', color: '#000' } })((props) => (
    <Chip color="default" {...props} />
));
const RedChip = withStyles({ root: { backgroundColor: '#FCCACD', color: '#000' } })((props) => (
    <Chip color="default" {...props} />
));
const GreenChip = withStyles({ root: { backgroundColor: '#95FCDE', color: '#000' } })((props) => (
    <Chip color="default" {...props} />
));

function renderStatus(params) {
    switch (params.value) {
        case 1:
            return <YellowChip label="Activa" size="small" />;
        case 2:
            return <RedChip label="Reparando" size="small" />;
        case 3:
            return <GreenChip label="Completada" size="small" />;
        default:
            return null;
    }
}

function CustomNoRowsOverlay() {
    return (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body1" color="textSecondary">
                No hay llamadas Andon
            </Typography>
        </Box>
    );
}

function formatElapsed(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = String(Math.floor(total / 3600)).padStart(2, '0');
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
}

const staticColumns = [
    {
        field: 'codigoArea',
        headerName: 'Area',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: renderArea,
    },
    {
        field: 'nombre',
        headerName: 'Nombre',
        flex: 2,
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'celda',
        headerName: 'Celda',
        flex: 1.5,
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'estatus',
        headerName: 'Estatus',
        flex: 1.5,
        align: 'center',
        headerAlign: 'center',
        renderCell: renderStatus,
    },
    {
        field: 'nivel',
        headerName: 'Nivel',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
    },
    {
        field: 'fechaHoraInicio',
        headerName: 'Inicio',
        flex: 1.5,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => {
            if (!params.value) return '-';
            const date = new Date(params.value);
            const h = date.getHours() % 12 || 12;
            const m = String(date.getMinutes()).padStart(2, '0');
            const s = String(date.getSeconds()).padStart(2, '0');
            const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
            return <span>{`${h}:${m}:${s} ${ampm}`}</span>;
        },
    },
];

export default function TablaAndon() {
    const { llamadasActivas } = useMonitoreo();
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const ticker = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(ticker);
    }, []);

    const visibleRows = useMemo(
        () => llamadasActivas.filter((l) => l.estatus !== 3),
        [llamadasActivas]
    );

    const renderTiempoRespuesta = useCallback(
        (params) => {
            const { estatus, fechaHoraInicio, fechaHoraRespuesta } = params.row;
            if (
                estatus === 2 &&
                fechaHoraRespuesta instanceof Date &&
                fechaHoraInicio instanceof Date
            ) {
                // Reparando: tiempo de respuesta fijo (de inicio a respuesta)
                return (
                    <span>
                        {formatElapsed(fechaHoraRespuesta.getTime() - fechaHoraInicio.getTime())}
                    </span>
                );
            }
            // Activa: reloj vivo desde el inicio
            const start = fechaHoraInicio instanceof Date ? fechaHoraInicio.getTime() : now;
            return <span>{formatElapsed(now - start)}</span>;
        },
        [now]
    );

    const renderTiempoReparacion = useCallback(
        (params) => {
            const { estatus, fechaHoraRespuesta } = params.row;
            if (estatus === 2 && fechaHoraRespuesta instanceof Date) {
                // Reparando: reloj vivo desde que respondieron
                return <span>{formatElapsed(now - fechaHoraRespuesta.getTime())}</span>;
            }
            // Activa: aún no hay respuesta
            return <span>00:00:00</span>;
        },
        [now]
    );

    const getRowClassName = useCallback(
        (params) => {
            const { estatus, fechaHoraInicio, fechaHoraRespuesta } = params.row;
            if (estatus === 1 && fechaHoraInicio instanceof Date) {
                return (now - fechaHoraInicio.getTime()) / 60000 > 15 ? 'andon-row--pulse-red' : '';
            }
            if (estatus === 2 && fechaHoraRespuesta instanceof Date) {
                return (now - fechaHoraRespuesta.getTime()) / 60000 > 15
                    ? 'andon-row--pulse-red'
                    : '';
            }
            return '';
        },
        [now]
    );

    const tableColumns = useMemo(
        () => [
            ...staticColumns,
            {
                field: 'tiempoRespuesta',
                headerName: 'T. Respuesta',
                flex: 1.5,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: renderTiempoRespuesta,
            },
            {
                field: 'tiempoReparacion',
                headerName: 'T. Reparación',
                flex: 1.5,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: renderTiempoReparacion,
            },
        ],
        [renderTiempoRespuesta, renderTiempoReparacion]
    );

    return (
        <div className="andon-table-wrapper">
            <DataGrid
                className="andon-datagrid-full"
                disableColumnFilter
                disableDensitySelector
                disableColumnMenu
                checkboxSelection={false}
                columns={tableColumns}
                localeText={esES.props.MuiDataGrid.localeText}
                rows={visibleRows}
                hideFooterPagination
                hideFooter
                getRowClassName={getRowClassName}
                components={{ NoRowsOverlay: CustomNoRowsOverlay }}
            />
        </div>
    );
}
